<template>
  <div 
    :style="{
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`,
      padding: `${dimensions.margin}px`,
    }"
    class="label_container"
    :class="`rotation-${dimensions.rotation}`"
    >
    <!-- QR + text share the top row, the barcode gets a full width strip below.
         Flex handles whether the QR sits beside the text or the text wraps. -->
    <div class="label_top" :style="{ height: `${top_height}px` }">
      <img
        v-if="show_qr && qrDataUrl"
        :src="qrDataUrl"
        :style="{ height: `${qr_size}px`, width: `${qr_size}px` }"
        />
      <div
        class="text-center textsection"
        style="overflow: hidden; flex: 1; min-width: 0;"
      >
        <p class="text-body mb-0" translate="no">
          {{ location.shelve_name }} {{ location.shelf | LetterFilter }} {{ location.slot | OneIndexFilter }} {{ location.product | LetterFilter }}
        </p>
        <p class="text-body" style="font-size: 10px">
          {{ get_product_title(product) || (get_product(product) ? get_product(product).title : 'Unknown') }}
        </p>
      </div>
    </div>
    
    <img
      v-if="show_barcode && barcodeDataUrl"
      :src="barcodeDataUrl"
      class="barcode_img"
      :style="{ height: `${barcode_height}px`, width: '100%' }"
      />
  </div>
</template>

<script>
import { OneIndexFilter, LetterFilter } from '@/utils/filters'
import { mapGetters } from 'vuex'
import QRCode from 'qrcode'
import JsBarcode from 'jsbarcode'

export default {
  computed: {
    ...mapGetters({
      get_product_location_by_uuid: 'warehouse/get_product_location_by_uuid',
      get_product: 'product/get_product',
      get_product_title: 'warehouse/get_product_title'
    }),
    location(){
      return this.get_product_location_by_uuid(this.product)
    },
    encode_value(){
      // New labels encode the short shelf code, old product uuids keep working
      // as a fallback while a code hasn't been generated yet.
      return (this.location && this.location.shelf_code) || this.product
    },
    show_qr(){
      return this.dimensions.show_qr !== false
    },
    show_barcode(){
      return this.dimensions.show_barcode === true
    },
    inner_width(){
      return this.dimensions.width - (this.dimensions.margin * 2)
    },
    inner_height(){
      return this.dimensions.height - (this.dimensions.margin * 2)
    },
    barcode_height(){
      if(!this.show_barcode) return 0
      return Math.max(24, Math.round(this.inner_height * 0.35))
    },
    top_height(){
      return this.inner_height - this.barcode_height
    },
    qr_size(){
      return Math.min(this.top_height, this.inner_width / 2)
    }
  },
  filters: {
    OneIndexFilter,
    LetterFilter,
  },
  props: {
    product: {
      type: String,
      required: true,
    },
    dimensions: {
      type: Object,
      required: true,
    }
  },
  data(){
    return {
      qrDataUrl: '',
      barcodeDataUrl: '',
    }
  },
  watch: {
    encode_value: {
      handler: 'render_codes',
      immediate: true,
    },
    show_qr: 'render_codes',
    show_barcode: 'render_codes',
  },
  methods: {
    async render_codes(){
      if(this.show_qr){
        this.qrDataUrl = await QRCode.toDataURL(this.encode_value, {
          errorCorrectionLevel: 'M',
          width: this.qr_size,
          margin: 0,
        })
      }
      if(this.show_barcode){
        try {
          const canvas = document.createElement('canvas')
          JsBarcode(canvas, this.encode_value, {
            format: 'CODE128',
            displayValue: false,
            margin: 8,
            height: 60,
            width: 2,
          })
          this.barcodeDataUrl = canvas.toDataURL()
        } catch (e) {
          console.error('Could not render barcode', e)
          this.barcodeDataUrl = ''
        }
      }
    }
  },
}
</script>
<style lang="scss" scoped>
 .label_container{
   display: flex;
   flex-direction: column;
   .label_top {
     display: flex;
     flex-direction: row;
     align-items: center;
     min-height: 0;
   }
   .textsection {
    text-align: center !important;
    font-family: "Boing", boing, sans-serif !important;
    font-style: normal;
    font-weight: normal;
    color: #000;
   }
   .barcode_img {
     display: block;
     // Uniform horizontal scaling keeps the bar width ratios intact.
     object-fit: fill;
   }
 }

 .rotation-0 {
   transform: rotate(0deg);
 }

.rotation-90 {
   transform: rotate(90deg);
 }
 .rotation-180 {
   transform: rotate(180deg);
 }
 .rotation-270 {
   transform: rotate(270deg);
 }
</style>
